import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Container,
  Chip,
} from '@mui/material';
import {
  Assessment as ReportsIcon,
  Security as LicenseCheckIcon,
  Work as PSPIcon,
  Language as LanguageIcon,
  Fingerprint as BackgroundCheckIcon,
  Description as ResumeIcon,
} from '@mui/icons-material';

interface Service {
  id: string;
  title: string;
  description: string;
  icon: React.ReactElement;
  path: string;
  tags?: string[];
  featured?: boolean;
}

const services: Service[] = [
  {
    id: 'reports',
    title: 'Reports',
    description: 'Create and manage DriverVerified reports for yourself or a team.',
    icon: <ReportsIcon />,
    path: '/services/reports',
  },
  {
    id: 'license-check',
    title: "Driver's License Status Check",
    description: 'Instantly check the validity of any Canadian driver\'s licence.',
    icon: <LicenseCheckIcon />,
    path: '/services/license-check',
  },
  {
    id: 'psp-reports',
    title: 'PSP Report Service',
    description: 'Pre-Employment Screening Program (PSP) Reports provides carriers access to American commercial driver safety records from the Federal Motor Carrier Safety Administration\'s (FMCSA) Motor Carrier Management Information System (MCMIS).',
    icon: <PSPIcon />,
    path: '/services/psp-reports',
  },
  {
    id: 'language-test',
    title: 'English Language Proficiency Test',
    description: 'Our English Language Proficiency Test service is an online test that measures English language ability in listening, speaking, writing, and conversing.',
    icon: <LanguageIcon />,
    path: '/services/language-test',
  },
  {
    id: 'background-checks',
    title: "Driver's Abstracts and Background Checks",
    description: 'Order government documents for commercial drivers and fleets, including Driver\'s Abstracts/Motor Vehicle Abstracts (MVRs), CVORDR, Criminal Record Checks, and Employment Reference.',
    icon: <BackgroundCheckIcon />,
    path: '/services/background-checks',
  },
  {
    id: 'resume',
    title: 'Resume',
    description: 'Customize and download a basic resume that is generated using your DriverVerified Profile.',
    icon: <ResumeIcon />,
    path: '/services/resume',
  },
];

export const ServicesList = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          Services
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Professional driver verification and compliance services
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {services.map((service) => (
          <Grid item xs={12} md={6} lg={4} key={service.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                border: '1px solid #e0e0e0',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                  borderColor: '#1976d2',
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      backgroundColor: '#1976d2',
                      color: 'white',
                      mr: 2,
                    }}
                  >
                    {service.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                      {service.title}
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary">
                  {service.description}
                </Typography>
              </CardContent>

              <CardActions sx={{ p: 3, pt: 0 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate(service.path)}
                  sx={{
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                    },
                  }}
                >
                  Product details
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}; 